/**
 * THE STONE LADDER — one file, every surface (P091 D-073, PO 2026-09-20).
 *
 *   Free USD 0  ·  Starter USD 49  ·  Growth USD 129  ·  Scale USD 279
 *
 * A number anywhere in the product that disagrees with this file is a DEFECT, not a variant.
 * Put it here, not in a component — that is the whole point of the stone.
 *
 * Shape copied from glowtest_FE `src/components/ui/monetization.js` on the PO's instruction
 * (2026-09-20: "Should work like glowtest_FE. Basically similar, let's not reinvent the wheel"):
 * the plans are DATA, each plan's Stripe Payment Link is DATA, and the dialog renders whatever
 * this file says. Adding a plan or changing a price is an edit HERE, never a component change.
 *
 * WHAT IS SOLD vs WHAT IS SHOWN (PO 2026-09-20, D-074):
 *   - **WE CHARGE PER EMAIL, NOT PER MEETING.** A tier buys CREDITS, and credits are the platform's
 *     currency: they spend on investor email AND on everything else (a mailbox-month, a domain, any
 *     future package). That is the whole reason for the peg: 1 USD = 1,000 credits, one currency,
 *     every action priced in it.
 *   - `creditsPerMonth` is WHAT YOU BUY — a commitment, spendable anywhere in the platform.
 *   - `estReplies` / `estMeetings` are what we SHOW, clearly labelled "est.". They are not a charge.
 *
 * THE TWO NUMBERS PER PLAN, AND WHY THE GRANT IS THE PRICE (PO 2026-09-20, D-075):
 *   - `creditsPerMonth` = the PRICE in credits (49,000 for $49). That is what "1 USD = 1,000 credits"
 *     means when a customer BUYS credits, so a payment of $49 must grant 49,000 and not one credit more
 *     or fewer. An earlier draft granted the reachout allowance instead (50 x 1,000 = 50,000) — $50 of
 *     credits for $49, a silent 2% over-grant on every sale.
 *   - `creditsPerReachout` = the PRICE OF ONE REACHOUT, and it FALLS as the plan rises
 *     (196 / 172 / 164). THE VOLUME DISCOUNT LIVES HERE, NOT IN THE GRANT — which is why the same
 *     49,000 credits buy 250 reachouts on Starter and the same 129,000 buy 750 on Growth.
 *   Both are STONE numbers and the BE kernel is their other home
 *   (`raisy_BE/cfg/raisy_subscription_plans.json`); a third place stating either is a defect.
 */

/** THE PUBLISHED CURRENCY IS USD (PO 2026-09-20: "i can set to USD"). It is the same currency
 *  the peg is defined in (1 USD = 1,000 credits) and the same one the competitor comparison is in, so
 *  no table anywhere has to convert. An earlier draft published EUR and carried an exchange-rate
 *  assumption through the plan table, the estimate table, the comparison and the chart; publishing in
 *  USD removes it, which is the same reason the credit peg is defined in USD.
 */

/** The two published bands the estimates come from. Midpoints, per D-072. */
export const EST_REPLY_RATE = 0.065; // midpoint of the 3-10% cold INVESTOR outreach reply band
export const EST_MEETING_RATE_OF_REPLIES = 0.4; // midpoint of the 30-50% reply -> meeting band
/** meetings as a share of investor reachouts: 6.5% x 40% = 2.6% */
export const EST_MEETING_RATE_OF_REACHOUTS = EST_REPLY_RATE * EST_MEETING_RATE_OF_REPLIES;

/** THE PEG — one currency for the whole platform. 1 USD = 1,000 credits; 1 credit = $0.001. */
export const CREDITS_PER_USD = 1000;
/** THE FALLBACK rate for one REACHOUT when the payer's tier cannot be resolved — it is NOT any
 *  tier's price. It is deliberately the DEAREST number on the ladder (1,000 credits = $1.00 an email,
 *  the pre-2026-09-20 price), so an unresolvable tier is over-charged at worst and refunded, where
 *  charging it the cheapest would under-charge on every single send. Mirrors the BE kernel's
 *  `per_send_credit_cost`.
 *
 *  ⚠️ IT IS **NOT** THE FREE TIER'S PRICE, and until 2026-09-21 it happened to be exactly that — which
 *  is how the two got confused in the first place. A free account is priced at the ENTRY rate (196),
 *  the same as Starter, because a free account is a Starter-in-waiting; the kernel declares it at
 *  `tiers.free.per_reachout_credits`. Every payer's real rate arrives from the server
 *  (`per_send_credit_cost`), so nothing in this app prices a send from this constant.
 *
 *  THE UNIT IS THE REACHOUT, NEVER "emails" (RULES_MASTER-adjacent law: RAISY_01_MANIFEST MR20 — one
 *  investor reached out to once, THROUGH ANY CHANNEL). The word "email" stays where the MESSAGE is
 *  meant (a tailored email written for each investor); it is not the unit we count, price or debit.
 *  That is also why the BE kernel's field is `per_reachout_credits`: one concept, one name, both repos. */
export const CREDITS_PER_REACHOUT = 1000;

export type PlanKey = "starter" | "growth" | "scale";

/**
 * IS THIS PAYER ON A PLAN? The one predicate the plan-gated surfaces ask (P092 T0200–T0202).
 *
 * Readings, verified against the server rather than assumed (2026-10-04):
 *   - `"free"`  — the backend's own word for "no grant". This IS a free payer → gate.
 *   - a plan key — paid → never gate.
 *   - `undefined` — the plan has NOT been read yet. This is NOT free (`MonetizationProvider`:
 *     "undefined here means unknown, NOT free"). A gate that fires on unknown would wall a
 *     paying customer whenever a balance request was slow — the worse of the two failures.
 *   - anything else — an unrecognised value; treated as NOT free, so a server-side rename
 *     can never silently start paywalling people.
 */
export function isFreeTier(tier: string | null | undefined): boolean {
  return tier === "free";
}

export interface Plan {
  key: PlanKey;
  name: string;
  /** WHAT YOU BUY — a commitment, in the published currency (USD). */
  monthlyPriceUsd: number;
  /** WHAT YOU BUY: credits, spendable on ANY action in the platform — not on one product.
   *  Equals the monthly price in USD x 1,000 — the price, expressed in the platform's currency. */
  creditsPerMonth: number;
  /** The PRICE OF ONE REACHOUT on this plan, in credits — the unit MR20 defines. FALLS as the plan
   *  rises (196 / 172 / 164): this is where the volume discount lives, NOT in the grant. */
  creditsPerReachout: number;
  /** What those credits buy in investor reachouts — DERIVED, never typed (see `definedPlan`). */
  reachoutsPerMonth: number;
  /** Price per 1,000 credits — DERIVED from `creditsPerReachout`. */
  usdPerThousandCredits: number;
  tagline: string;
  features: string[];
  /** The Stripe Payment Link. Minted by the PO (F1); empty until then. */
  stripeUrl: string;
  /** The amount Stripe must charge for this tier, in cents — the webhook's tier identifier. */
  amountCents: number;
  /** Rendered first / highlighted. */
  popular?: boolean;
}

/** Build a plan from the TWO stone numbers only.
 *
 * `reachoutsPerMonth` (credits ÷ price per reachout) and `usdPerThousandCredits` (the per-reachout price
 * in dollars) are DERIVED here rather than typed a second time in each plan object. Two homes for one
 * fact is exactly how a ladder drifts apart from itself, and the BE kernel is already the other home
 * for the two real numbers — so the derived ones are computed, and cannot disagree.
 */
type PlanNumbers = Omit<Plan, "reachoutsPerMonth" | "usdPerThousandCredits">;
const definedPlan = (p: PlanNumbers): Plan => {
  const reachoutsPerMonth = Math.round(p.creditsPerMonth / p.creditsPerReachout);
  const estMeetingsPerMonth =
    Math.round(reachoutsPerMonth * EST_REPLY_RATE * EST_MEETING_RATE_OF_REPLIES * 10) / 10;
  return {
    ...p,
    reachoutsPerMonth,
    usdPerThousandCredits: p.creditsPerReachout / CREDITS_PER_USD,
    // THE PROMISE LEADS WITH THE OUTCOME, THE MECHANISM FOLLOWS (PO 2026-09-20, after the persona
    // read). Maya does not buy emails — her own evidence file says warm beats cold by ~13x and that
    // optimising a cold email is tuning the small term, and her expectation for this surface is "the
    // meeting is the output, not the email". So the first thing a plan says is the meetings it is
    // estimated to produce, and the reachout count is stated as how that is produced. DERIVED from the
    // stone, never typed: a typed number here is a second home for a figure the rate already decides.
    features: [
      `About ${estMeetingsPerMonth} investor meetings a month, from ${reachoutsPerMonth.toLocaleString(
        "en-IE",
      )} investor reachouts we write and send for you`,
      ...p.features,
    ],
  };
};

// ---------------------------------------------------------------- estimates (never a promise)
export const estReplies = (p: Plan): number => round1(p.reachoutsPerMonth * EST_REPLY_RATE);
export const estMeetings = (p: Plan): number => round1(estReplies(p) * EST_MEETING_RATE_OF_REPLIES);
export const estCostPerMeeting = (p: Plan): number => {
  const m = estMeetings(p);
  if (m <= 0) return 0;
  return Math.round(p.monthlyPriceUsd / m);
};
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

// ---------------------------------------------------------------- Stripe (env first, like glowtest)
const FALLBACK_STRIPE_STARTER = "";
const FALLBACK_STRIPE_GROWTH = "";
const FALLBACK_STRIPE_SCALE = "";
const FALLBACK_STRIPE_CREDITS = "";

export const STRIPE_STARTER = import.meta.env.VITE_STRIPE_STARTER || FALLBACK_STRIPE_STARTER;
export const STRIPE_GROWTH = import.meta.env.VITE_STRIPE_GROWTH || FALLBACK_STRIPE_GROWTH;
export const STRIPE_SCALE = import.meta.env.VITE_STRIPE_SCALE || FALLBACK_STRIPE_SCALE;
export const STRIPE_CREDITS = import.meta.env.VITE_STRIPE_CREDITS || FALLBACK_STRIPE_CREDITS;

/** THE AMOUNTS THE CARD OFFERS, in whole USD — DATA, like the plans, so adding $250 is an edit here
 *  rather than a component change.
 *
 *  They sit INSIDE the backend's floor and ceiling ($5-$1,000, refused before Stripe is called), and
 *  they are round because the peg is: at 1 USD = 1,000 credits each one is a round number of credits,
 *  which a stray $37.50 would not be. */
export const CREDIT_TOPUP_AMOUNTS_USD = [10, 25, 50, 100];

/** Where the card opens: the middle of that range, so the first thing a buyer sees is a real choice and
 *  not our smallest or largest offer. */
export const CREDIT_TOPUP_DEFAULT_USD = 25;

export const PLANS: Plan[] = [
  definedPlan({
    key: "starter",
    creditsPerMonth: 49_000, // $49 x 1,000
    creditsPerReachout: 196, // 49,000 / 196 = 250 reachouts, as sold
    name: "Starter",
    monthlyPriceUsd: 49,
    tagline: "Your first real month of outreach",
    features: [
      "A tailored email written for each investor",
      "The compliance gate on every send",
      "Verified or replaced: any hard bounce, replaced free",
      "Warm-up carried by us",
    ],
    stripeUrl: STRIPE_STARTER,
    amountCents: 4900,
  }),
  definedPlan({
    key: "growth",
    creditsPerMonth: 129_000, // $129 x 1,000
    creditsPerReachout: 172, // 129,000 / 172 = 750 reachouts, as sold
    name: "Growth",
    monthlyPriceUsd: 129,
    tagline: "A full seed-round outreach month",
    popular: true,
    features: [
      "A tailored email written for each investor",
      "The compliance gate on every send",
      "Verified or replaced, plus the Evidence Trail",
      "First-Conversation Guarantee: one investor response in your first 200 reachouts, or we keep sending free",
    ],
    stripeUrl: STRIPE_GROWTH,
    amountCents: 12900,
  }),
  definedPlan({
    key: "scale",
    creditsPerMonth: 279_000, // $279 x 1,000
    creditsPerReachout: 164, // 279,000 / 164 = 1,701, published as 1,700
    name: "Scale",
    monthlyPriceUsd: 279,
    tagline: "Continuous, high-volume outreach",
    features: [
      "A tailored email written for each investor",
      "The compliance gate on every send",
      "Verified or replaced, plus the Evidence Trail",
      "First-Conversation Guarantee: one investor response in your first 200 reachouts, or we keep sending free",
    ],
    stripeUrl: STRIPE_SCALE,
    amountCents: 27900,
  }),
];

/** True while the PO has not yet minted the Payment Links — the dialog says so instead of
 *  rendering a button that goes nowhere. */
export const paymentLinksReady = PLANS.every((p) => Boolean(p.stripeUrl));

export const usd = (n: number): string => `$${n.toLocaleString("en-US")}`;

/**
 * THE ONE WAY A CREDIT FIGURE IS WRITTEN (P091, PO 2026-09-21: *"also the format of the FE in terms
 * of numbering. same thing."*).
 *
 * It is glowtest_FE's own expression for its flag — `parseFloat(n.toFixed(1)).toLocaleString()`: at
 * most ONE decimal (a credit is not a currency, but the balance is a float server-side, so the
 * rounding has to be STATED rather than left to whatever the number happens to be), then locale
 * separators, and no locale is forced, which is what glowtest does.
 *
 * WHY IT LIVES HERE, beside `usd`: the figure was written by hand in NINE places, in a mixture of
 * locales, so the same balance could be punctuated two ways on two screens. A format restated per
 * component is a format that drifts — the same reason the ladder's numbers live here.
 */
export const formatCredits = (n: number): string => parseFloat(n.toFixed(1)).toLocaleString();

/** THE ENTRY RATE — the ladder's first rung, and what a balance spends at once the plan has ended.
 *
 * DERIVED from `PLANS[0]`, never typed. D-107 settled on 2026-09-23 that the leftover balance keeps
 * spending at the entry rate rather than at the rate it was bought at (which re-introduces exactly the
 * per-plan credit value D-079 refused, and has no single answer once a top-up is mingled in) or at
 * zero (which contradicts the peg — credits are "what you buy, spendable anywhere"). D-081 has already
 * re-based these rates once, 5x down; a typed 196 here would have survived that re-basing as a lie. */
export const ENTRY_REACHOUT_CREDITS = PLANS[0].creditsPerReachout;

/** The ADD-CREDITS card — a one-time addition to the same credit balance (D-060).
 *
 * The amount is the CUSTOMER'S to choose (the Stripe price is pay-what-you-want, which is what the
 * editable field at checkout is), so this card states the RATE instead of a price — the same
 * "1 USD = 1,000 credits" the blog and the plan cards use. It said "$ Credits" until 2026-09-20, which
 * left the one number a buyer needs in order to know what he is buying unstated, on the only surface
 * where he is asked to pay for it.
 *
 * AND IT SAID "BUY CREDITS" UNTIL 2026-09-21 (PO: *"add credits instead of 'buy credits' plz"*).
 * "Buy" names the transaction and the reader is the one being sold to; "Add" names the effect on his
 * own balance, which is the same thing the refusal message at the send gate already says ("Add credits
 * to keep sending", D-093). One action, one name — see D-099 for the two naming gates this passed.
 *
 * IT LIVES AT THE END OF THE FILE BECAUSE IT READS THE LADDER (2026-09-23, D-107). The second line used
 * to say "Spent at your plan's own price per reachout" and stop there — true while a plan is active, and
 * SILENT about the one case a buyer cannot see: his plan ends and the leftover spends at the entry rate
 * instead of the rate he was buying at (a Growth customer loses 92 reachouts off a leftover month). The
 * sentence now states both, in the file's own vocabulary ("the entry rate", which this file already
 * uses), with the number DERIVED from the ladder. Honesty row of the layer table, paid for with one
 * clause: the reader learns what happens to credits he has already bought, before he buys them. */
export const CREDITS_OPTION = {
  name: "Add credits",
  priceLabel: "1 USD = 1,000 credits",
  // NO `billing` LINE ANY MORE: it described the amount choice, and the choice is now ON the card — the
  // one thing a card cannot need is a sentence about what it already shows.
  features: [
    "Lands in the same balance your plan uses",
    `Spent at your plan's price per reachout — the entry rate of ${formatCredits(
      ENTRY_REACHOUT_CREDITS,
    )} once your plan has ended`,
    "No subscription, nothing to cancel",
  ],
  url: STRIPE_CREDITS,
};
